import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  type ErrorComponentProps,
  Navigate,
  stripSearchParams,
} from "@tanstack/react-router";
import { Suspense } from "react";
import { useProjectPermissions } from "@/app/capabilities/api/hooks";
import { PROJECT } from "@/app/capabilities/permissions";
import { mcpGatewayQueryOptions } from "@/app/mcp-gateways/api/get-mcp-gateway";
import {
  GatewayEditPage,
  GatewayFormSkeleton,
} from "@/app/mcp-gateways/create-page/gateway-create-page";
import { gatewayFormTitle } from "@/app/mcp-gateways/create-page/gateway-form-access";
import {
  mcpGatewayEditSearchDefaults,
  mcpGatewayEditSearchSchema,
} from "@/app/mcp-gateways/search-params";
import type { ScopedBinding } from "@/app/types";
import { CustomErrorComponent } from "@/components/custom/router/custom-error";
import { isHttpError } from "@/lib/http-error";
import { requireProjectPermission } from "@/lib/route-guards";

export const Route = createFileRoute(
  "/_authenticated/orgs/$organizationId/projects/$projectId/mcp-gateways/$gatewayId/edit"
)({
  validateSearch: mcpGatewayEditSearchSchema,
  search: {
    middlewares: [stripSearchParams(mcpGatewayEditSearchDefaults)],
  },
  loaderDeps: ({ search }) => ({ binding: search.binding }),
  // Readers open the same page to inspect configuration. The form locks itself
  // when they lack update, so the route only requires the read.
  beforeLoad: async (ctx) => {
    await requireProjectPermission(ctx, PROJECT.mcpGateways.read);
    // The form copies the gateway once, when it mounts. A copy invalidated by
    // a gateway write is dropped, even while its refetch runs, so the form
    // waits behind its skeleton for the saved gateway instead of starting from
    // the old one. This runs here because the router skips the loader when it
    // reuses a recent visit.
    const { queryKey } = gatewayQuery(ctx.params, ctx.search.binding);
    if (ctx.context.queryClient.getQueryState(queryKey)?.isInvalidated) {
      ctx.context.queryClient.removeQueries({ queryKey, exact: true });
    }
  },
  loader: ({ context: { queryClient }, params, deps }) => {
    queryClient.prefetchQuery(gatewayQuery(params, deps.binding));
    return { crumb: "Edit gateway" };
  },
  component: RouteComponent,
  errorComponent: GatewayEditError,
});

function RouteComponent() {
  const { canProject } = useProjectPermissions();
  const title = gatewayFormTitle(true, !canProject(PROJECT.mcpGateways.update));
  return (
    <Suspense fallback={<GatewayFormSkeleton title={title} />}>
      <GatewayEditForm />
    </Suspense>
  );
}

function GatewayEditForm() {
  const params = Route.useParams();
  const { binding } = Route.useSearch();
  const { data: gateway } = useSuspenseQuery(gatewayQuery(params, binding));

  return <GatewayEditPage gateway={gateway} key={gateway.id} />;
}

function gatewayQuery(
  params: { organizationId: string; projectId: string; gatewayId: string },
  binding: ScopedBinding | undefined
) {
  return mcpGatewayQueryOptions({
    binding,
    gatewayId: params.gatewayId,
    scope: {
      organizationId: params.organizationId,
      projectId: params.projectId,
    },
  });
}

// A gateway that no longer exists sends the user back to the list.
function GatewayEditError(props: ErrorComponentProps) {
  const { organizationId, projectId } = Route.useParams();
  if (isHttpError(props.error) && props.error.status === 404) {
    return (
      <Navigate
        params={{ organizationId, projectId }}
        replace
        to="/orgs/$organizationId/projects/$projectId/mcp-gateways"
      />
    );
  }
  return <CustomErrorComponent {...props} />;
}
